import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { TextField } from "@material-ui/core";
import { Autocomplete } from "@material-ui/lab";

import { TEXT_MATCH } from "../../constants/data";

export const TYPING_DELAY_MS = 400;

/**
 * Free-text filter with suggestions. Picking a suggestion filters by that exact value; typed
 * text filters by "contains", but only once the admin pauses, so a half-typed "I" is never sent
 * on the way to "II". Reports `{value, match}`; `value` is null when cleared.
 */
const MatchFilter = ({ label, options, value, match, onChange, className }) => {
  const [draft, setDraft] = useState(value || "");
  const timer = useRef(null);

  // Follow outside changes (reset filters, back/forward navigation).
  useEffect(() => {
    setDraft(value || "");
  }, [value]);

  useEffect(() => () => clearTimeout(timer.current), []);

  const emit = (next, nextMatch) => {
    clearTimeout(timer.current);
    const trimmed = (next || "").trim();
    if (!trimmed) {
      if (value) onChange({ value: null, match: TEXT_MATCH.EXACT });
      return;
    }
    if (trimmed === (value || "") && nextMatch === match) return;
    onChange({ value: trimmed, match: nextMatch });
  };

  const helper = value
    ? match === TEXT_MATCH.CONTAINS
      ? `Contains “${value}”`
      : `Exactly “${value}”`
    : " ";

  return (
    <Autocomplete
      freeSolo
      className={className}
      options={options || []}
      value={value || null}
      inputValue={draft}
      onInputChange={(_, next, reason) => {
        if (reason !== "input") return;
        setDraft(next);
        clearTimeout(timer.current);
        timer.current = setTimeout(
          () => emit(next, TEXT_MATCH.CONTAINS),
          TYPING_DELAY_MS,
        );
      }}
      onChange={(_, next, reason) => {
        if (reason === "clear" || next == null) {
          setDraft("");
          emit("", TEXT_MATCH.EXACT);
          return;
        }
        setDraft(next);
        emit(
          next,
          reason === "select-option" ? TEXT_MATCH.EXACT : TEXT_MATCH.CONTAINS,
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          variant="outlined"
          size="small"
          helperText={helper}
        />
      )}
    />
  );
};

MatchFilter.propTypes = {
  label: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(PropTypes.string),
  value: PropTypes.string,
  match: PropTypes.oneOf([TEXT_MATCH.EXACT, TEXT_MATCH.CONTAINS]),
  onChange: PropTypes.func.isRequired,
  className: PropTypes.string,
};

MatchFilter.defaultProps = {
  options: [],
  value: "",
  match: TEXT_MATCH.EXACT,
  className: undefined,
};

export default MatchFilter;
